<template>
  <div class="space-y-6">
    <div class="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
      <div class="flex items-center justify-between mb-1">
        <h2 class="text-lg font-bold text-neutral-900">上传大小设置</h2>
        <button
          @click="load"
          class="px-3 py-2 rounded-lg border border-neutral-300 hover:border-primary hover:text-primary transition-colors"
          title="刷新"
        >
          <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
        </button>
      </div>
      <p class="text-sm text-neutral-500">
        控制站内各类文件的上传大小上限，
        <span class="text-primary">Web 端与小程序端同步生效</span>
      </p>

      <div v-loading="loading" class="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">
            图片上传上限（MB）
          </label>
          <input
            v-model.number="form.imageMaxMB"
            type="number"
            min="1"
            :max="imageHardMaxMB"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
          />
          <p class="mt-1 text-xs text-neutral-400">
            工具封面、用户头像、首页轮播图等图片
          </p>
        </div>

        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">
            附件上传上限（MB）
          </label>
          <input
            v-model.number="form.fileMaxMB"
            type="number"
            min="1"
            :max="fileHardMaxMB"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
          />
          <p class="mt-1 text-xs text-neutral-400">
            工具附件（ZIP / HTML 包等），同时作为包内单文件上限
          </p>
        </div>

        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">
            ZIP 解压总大小上限（MB）
          </label>
          <input
            v-model.number="form.zipTotalMaxMB"
            type="number"
            min="1"
            :max="zipTotalUpperMB"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
          />
          <p class="mt-1 text-xs text-neutral-400">
            ZIP 工具包解压后所有文件的合计大小
          </p>
        </div>
      </div>

      <div class="mt-5 p-3 rounded-lg bg-amber-50 text-amber-700 text-xs leading-relaxed">
        提示：受服务器与网关限制，图片上限最多设到
        <b>{{ imageHardMaxMB }}MB</b>，附件上限最多设到
        <b>{{ fileHardMaxMB }}MB</b>；ZIP 解压总大小属于服务端磁盘限制，最多可设到
        <b>{{ zipTotalUpperMB }}MB</b>。改动保存后立即生效。
      </div>

      <div class="mt-6 flex justify-end">
        <button
          @click="save"
          :disabled="loading"
          class="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm font-medium disabled:opacity-50"
        >
          <i class="fa fa-save mr-1"></i>保存设置
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage } from "element-plus";

export default {
  name: "UploadLimitManager",
  data() {
    return {
      loading: false,
      imageHardMaxMB: 110,
      fileHardMaxMB: 200,
      zipTotalUpperMB: 2000,
      form: {
        imageMaxMB: 10,
        fileMaxMB: 100,
        zipTotalMaxMB: 500,
      },
    };
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await this.$axios({ url: "upload/config", method: "GET" });
        if (res.code === 200 && res.data) {
          this.form.imageMaxMB = res.data.imageMaxMB;
          this.form.fileMaxMB = res.data.fileMaxMB;
          this.form.zipTotalMaxMB = res.data.zipTotalMaxMB;
          this.imageHardMaxMB = res.data.imageHardMaxMB || this.imageHardMaxMB;
          this.fileHardMaxMB = res.data.fileHardMaxMB || this.fileHardMaxMB;
          this.zipTotalUpperMB = res.data.zipTotalUpperMB || this.zipTotalUpperMB;
        }
      } catch (e) {
        console.error("获取上传配置失败:", e);
      } finally {
        this.loading = false;
      }
    },
    async save() {
      const { imageMaxMB, fileMaxMB, zipTotalMaxMB } = this.form;
      if (!imageMaxMB || !fileMaxMB || !zipTotalMaxMB) {
        ElMessage.warning("请填写完整的上传大小限制");
        return;
      }
      if (imageMaxMB < 1 || imageMaxMB > this.imageHardMaxMB) {
        ElMessage.warning(`图片上传上限需在 1-${this.imageHardMaxMB}MB 之间`);
        return;
      }
      if (fileMaxMB < 1 || fileMaxMB > this.fileHardMaxMB) {
        ElMessage.warning(`附件上传上限需在 1-${this.fileHardMaxMB}MB 之间`);
        return;
      }
      if (zipTotalMaxMB < 1 || zipTotalMaxMB > this.zipTotalUpperMB) {
        ElMessage.warning(
          `ZIP 解压总大小上限需在 1-${this.zipTotalUpperMB}MB 之间`
        );
        return;
      }
      this.loading = true;
      try {
        const data = await this.$aesEncrypt(this.form);
        const res = await this.$axios({
          url: "upload/admin/config",
          method: "PUT",
          data: { data },
        });
        if (res.code === 200) {
          ElMessage.success("上传设置已保存，立即生效");
        } else {
          ElMessage.error(res.msg || "保存失败");
        }
      } catch (e) {
        ElMessage.error("保存失败，请重试");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
